/**
 * Navigation blocker for unsaved changes.
 * Uses React Router v7's useBlocker API to prevent navigation when there are unsaved changes.
 * Also handles browser tab close/refresh via beforeunload event.
 */

import { useEffect } from 'react'
import { useBlocker } from 'react-router'

const useLeavePageConfirm = (
  shouldConfirm: boolean,
  message: string,
  callback?: () => Promise<void>,
) => {
  // Block in-app navigations via React Router
  useBlocker(({ currentLocation, nextLocation }) => {
    if (shouldConfirm && currentLocation.pathname !== nextLocation.pathname) {
      const confirmed = window.confirm(message)
      if (confirmed && callback) {
        callback()
      }
      return !confirmed
    }
    return false
  })

  // Handle browser tab close/refresh
  useEffect(() => {
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (shouldConfirm) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [shouldConfirm])
}

/**
 * Use this prompt to ask the user if they want to leave the page when they have unsaved changes
 */
export function Prompt({ shouldConfirm, message, callback }: PromptProps) {
  useLeavePageConfirm(shouldConfirm, message, callback)
  return null
}

interface PromptProps {
  shouldConfirm: boolean
  message: string
  callback?: () => Promise<void>
}
